import { ChangeEvent, Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import Select from 'react-select';
import { toast } from 'react-toastify';
import useThemeContext from '../../../../../../contexts/theme';
import useApiCall from '../../../../../../hooks/useApiCall';
import useErrors from '../../../../../../hooks/useErrors';
import useGetGeneralAppData from '../../../../../../hooks/useGetGeneralAppData';
import { useQuery } from '../../../../../../hooks/useQuery';
import ConsultsService from '../../../../../../services/ConsultsService';
import floatToCurrency from '../../../../../../utils/floatToCurrency';
import onlyNumbers from '../../../../../../utils/onlyNumbers';
import removeDuplicates from '../../../../../../utils/removeDuplicates';
import { CustomStyle } from '../../../../../CustomSelectStyle';
import { CustomStyle as CustomStyleDarkTheme } from '../../../../../CustomSelectStyleDarkTheme';
import FilterRadioButton from '../../../../../FilterRadioButtons';
import { FilterRadioButtonsContainer } from '../../../../../FilterRadioButtonsContainer';
import FormGroup from '../../../../../FormGroup';
import Input from '../../../../../Input';
import MyModal from '../../../../../Modal';
import { ISelectedValue } from '../../../../types';
import { ICreateInstructionApiResponse, ListaCidRegiao, ListaItensVt, Rotas, TrajetoInstruc } from '../../types';
import { AsideContainer } from './styles';

interface IAddInstructionModal {
  addInstructionModalShow: boolean;
  setAddInstructionModalShow: Dispatch<SetStateAction<boolean>>;
  vtItensOptions: ListaItensVt[];
  regionsOptions: ListaCidRegiao[];
  direction: 'going' | 'returning';
  instructionsSeparatedByDirection: {
    going: TrajetoInstruc[],
    returning: TrajetoInstruc[]
  };
  setInstructions: Dispatch<SetStateAction<TrajetoInstruc[]>>;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  manualRoutingType: 'simple' | 'detailed';
  currentInstructions: TrajetoInstruc[];
  selectedRoute: Rotas;
  instructionsSeparatedByRoute: TrajetoInstruc[];
  isMultiRoute: boolean;
}

export default function AddInstructionModal({
  addInstructionModalShow,
  setAddInstructionModalShow,
  manualRoutingType,
  vtItensOptions,
  regionsOptions,
  direction,
  instructionsSeparatedByDirection,
  setInstructions,
  // currentInstructions,
  setIsLoading,
  selectedRoute,
  instructionsSeparatedByRoute,
  isMultiRoute,
}: IAddInstructionModal) {
  const [selectedUf, setSelectedUf] = useState({} as ISelectedValue);
  const [selectedItem, setSelectedItem] = useState({} as ISelectedValue);
  const [instruction, setInstruction] = useState<'Caminhada' | 'Transporte Público'>('Transporte Público');
  const [instructionOrigin, setInstructionOrigin] = useState('');
  const [instructionDestiny, setInstructionDestiny] = useState('');
  const [transportLine, setTransportLine] = useState('');
  const [instructionDistance, setInstructionDistance] = useState<string | number>('');
  const [instructionTime, setInstructionTime] = useState<string | number>('');

  const { selectedTheme } = useThemeContext();
  const { apiCall } = useApiCall();
  const { userCode, userProfile } = useGetGeneralAppData();
  const query = useQuery();
  const companyCode = query.get('codEmpresa');
  const { consultCode } = useParams();
  const {
    setError, removeError, getErrorMessageByFieldName, errors,
  } = useErrors();

  const regionsOptionsMapped = removeDuplicates(regionsOptions.map((opt) => ({
    value: opt.codUF, label: opt.uf
  })));

  const vtOptionsBySelectedUf = useMemo(() => {
    const selectedUfCode = selectedUf.value;
    const filteredVtsByUf = vtItensOptions.filter((vt) => ((vt.codUF === selectedUfCode || String(vt.codUF) === selectedUf.label)) || (vt.tipoVT === 2 || vt.codGeo === 'GR01'));
    const mappedVtOptions = filteredVtsByUf.map((opt) => (
      { value: opt.codItem, label: `${opt.descricao} - ${opt.codGeo} (${floatToCurrency(opt.tarifa)})`}
    ));

    return mappedVtOptions;
  }, [selectedUf.label, selectedUf.value, vtItensOptions]);

  function handleSelectedUfChange(opt: ISelectedValue) {
    setSelectedUf(opt);
  }

  function handleSelectedItemChange(opt: ISelectedValue) {
    setSelectedItem(opt);
  }

  function handleInstructionOriginChange(event: ChangeEvent<HTMLInputElement>) {
    setInstructionOrigin(event.target.value);

    if (!event.target.value) {
      setError({ field: 'instructionOrigin', message: 'Endereço de origem da instrução é obrigatório!' });
    } else if (event.target.value.toLocaleLowerCase() === 'local indefinido') {
      setError({ field: 'instructionOrigin', message: 'Endereço de origem não pode ser local indefinido!' });
    } else {
      removeError('instructionOrigin');
    }
  }

  function handleInstructionDestinyChange(event: ChangeEvent<HTMLInputElement>) {
    setInstructionDestiny(event.target.value);

    if (!event.target.value) {
      setError({ field: 'instructionDestiny', message: 'Endereço de destino da instrução é obrigatório!' });
    } else if (event.target.value.toLocaleLowerCase() === 'local indefinido') {
      setError({ field: 'instructionDestiny', message: 'Endereço de destino não pode ser local indefinido!' });
    } else {
      removeError('instructionDestiny');
    }
  }

  function handleTransportLineChange(event: ChangeEvent<HTMLInputElement>) {
    setTransportLine(event.target.value);

    if (!event.target.value) {
      setError({ field: 'transportLine', message: 'Linha de transporte é obrigatória!' });
    } else {
      removeError('transportLine');
    }
  }

  function handleInstructionDistanceChange(event: ChangeEvent<HTMLInputElement>) {
    setInstructionDistance(onlyNumbers(event.target.value));

    if (!event.target.value) {
      setError({ field: 'instructionDistance', message: 'Distância da instrução é obrigatório!' });
    } else {
      removeError('instructionDistance');
    }
  }

  function handleInstructionTimeChange(event: ChangeEvent<HTMLInputElement>) {
    setInstructionTime(onlyNumbers(event.target.value));

    if (!event.target.value) {
      setError({ field: 'instructionTime', message: 'Tempo da instrução é obrigatório!' });
    } else {
      removeError('instructionTime');
    }
  }

  const createInstruction = useCallback(async () => {
    const findCorrespondingVtItem = (vtItemCode: number) => (
      vtItensOptions.find((vt) => vt.codItem === vtItemCode)
    );

    const instructionToCodeLiteral: { [key: string]: number } = {
      'Transporte': 2,
      'Transporte Público': 2,
      'Caminhar': 1,
      'Caminhada': 1,
      'Abater Resultado': 3
    };

    const mapInstructions = (instruction: TrajetoInstruc[]) => {
      const mappedCurrentInstructions = (!instruction || instruction?.length === 0) ? [] : instruction.map((inst) => ({
        tipoInstrucao: instructionToCodeLiteral[inst.instrucao],
        instrucao: inst.instrucao,
        sequencia: inst.sequencia,
        origem: inst.origem,
        destino: inst.destino,
        linhaTransporte: inst.linhaTransp,
        routeID: '',
        tempo: inst.tempo,
        distancia: inst.distancia,
        origemLat: inst.origemLat,
        origemLng: inst.origemLng,
        destinoLat: inst.destinoLat,
        destinoLng: inst.destinoLng,
        codGeo: findCorrespondingVtItem(inst.codItem)?.codGeo || '',
        codItem: inst.codItem || 0,
        tarifa: findCorrespondingVtItem(inst.codItem)?.tarifa || 0,
      }));

      return mappedCurrentInstructions;
    };

    await apiCall({
      apiToCall: ConsultsService.createInstruction,
      reqBody: JSON.stringify({
        codUsu: userCode,
        perfilUsu: userProfile,
        codEmpresa: companyCode,
        codConsulta: consultCode,
        rota: isMultiRoute ? selectedRoute.rota : 1,
        sentido: isMultiRoute ? selectedRoute.sentido : direction === 'going' ? 1 : 2,
        trajetoInstrucoes: [
          ...mapInstructions(isMultiRoute ? instructionsSeparatedByRoute : instructionsSeparatedByDirection[direction]),
          {
            instrucao: manualRoutingType === 'detailed' ? instruction : 'Transporte Público',
            tipoInstrucao: manualRoutingType === 'detailed' ? instructionToCodeLiteral[instruction] : 2,
            sequencia: (isMultiRoute ? instructionsSeparatedByRoute?.length + 1 : instructionsSeparatedByDirection[direction]?.length + 1) || 1,
            origem:  manualRoutingType === 'detailed' ? instructionOrigin : 'Local indefinido',
            destino: manualRoutingType === 'detailed' ? instructionDestiny : 'Local indefinido',
            linhaTransporte: manualRoutingType === 'detailed' ? transportLine : findCorrespondingVtItem(selectedItem.value)?.descricao ?? '',
            routeID: '',
            tempo: manualRoutingType === 'detailed' ? instructionTime : 30,
            distancia: manualRoutingType === 'detailed' ? instructionDistance : 2000,
            origemLat: 0,
            origemLng: 0,
            destinoLat: 0,
            destinoLng: 0,
            codGeo: findCorrespondingVtItem(selectedItem.value)?.codGeo ?? '',
            codItem: selectedItem.value ?? '',
            tarifa: findCorrespondingVtItem(selectedItem.value)?.tarifa ?? 0,
          }
        ]
      }),
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      catchMessage: 'Não foi possível gravar a instrução. Por favor, tente novamente',
      actionAfterResponse: (response: ICreateInstructionApiResponse) => {
        if (!response.codigo) {
          toast.error(`Não foi possível gravar a instrução. Por favor, tente novamente (${response.msg})`);
          return;
        }
        setAddInstructionModalShow(false);
        toast.success('A instrução foi gravada com sucesso. Conclua a roteirização, ou adicione mais instruções!');
        setInstructions(response.trajetoInstrucoes);
      }
    });
  }, [apiCall, companyCode, consultCode, direction, instruction, instructionDestiny, instructionDistance, instructionOrigin, instructionTime, instructionsSeparatedByDirection, instructionsSeparatedByRoute, isMultiRoute, manualRoutingType, selectedItem?.value, selectedRoute?.rota, selectedRoute?.sentido, setAddInstructionModalShow, setInstructions, setIsLoading, transportLine, userCode, userProfile, vtItensOptions]);

  const isValidToCreateInstruction = manualRoutingType === 'simple' ? (!!selectedItem.value) : (
    ((instruction === 'Caminhada' && !!instructionOrigin && !!instructionDestiny && !!instructionDistance && !!instructionTime) || (instruction === 'Transporte Público' && !!transportLine && !!selectedItem.value && !!instructionOrigin && !!instructionDestiny && !!instructionDistance && !!instructionTime)) && (errors.length === 0)
  );

  const canAddWalking = useMemo(() => {
    const instructionsAtSelectedDirection = isMultiRoute ? instructionsSeparatedByRoute : instructionsSeparatedByDirection[direction];
    const lengthOfInstructionsAtDirections = isMultiRoute ? instructionsSeparatedByRoute?.length : instructionsSeparatedByDirection[direction]?.length;

    if (!instructionsAtSelectedDirection || lengthOfInstructionsAtDirections === 0) {
      return true;
    }

    const isLastInstructionWalking = isMultiRoute ? instructionsSeparatedByRoute[lengthOfInstructionsAtDirections - 1].instrucao === 'Caminhada' : instructionsSeparatedByDirection[direction][lengthOfInstructionsAtDirections - 1].instrucao === 'Caminhada';

    if (isLastInstructionWalking) {
      return false;
    }

    return true;
  }, [direction, instructionsSeparatedByDirection, instructionsSeparatedByRoute, isMultiRoute]);

  return (
    <MyModal
      show={addInstructionModalShow}
      title={manualRoutingType === 'simple' ? 'Adicionar Bilhete' : 'Adicionar Instrução'}
      closeButtonLabel="Fechar"
      actionButtonLabel={manualRoutingType === 'simple' ? 'Gravar Bilhete' : 'Gravar Instrução'}
      onClose={() => setAddInstructionModalShow(false)}
      onAction={() => createInstruction()}
      minHeight='400px'
      isActionButtonDisabled={!isValidToCreateInstruction}
      type="action"
      size='lg'
      modalBody={(
        <>
          {manualRoutingType === 'detailed' && (
            <FilterRadioButtonsContainer style={{ marginBottom: '8px'}}>
              <FilterRadioButton selected={instruction === 'Transporte Público'} onClick={() => setInstruction('Transporte Público')}>
                Transporte Público
              </FilterRadioButton>
              <FilterRadioButton selected={instruction === 'Caminhada'} onClick={() => setInstruction('Caminhada')} disabled={!canAddWalking}>
                Caminhada
              </FilterRadioButton>
            </FilterRadioButtonsContainer>
          )}

          {(manualRoutingType === 'simple' || instruction !== 'Caminhada') && (
            <>
              <FormGroup>
                <label htmlFor="contactName">Selecione a UF do VT</label>
                <Select
                  value={selectedUf}
                  options={regionsOptionsMapped}
                  onChange={(opt) => handleSelectedUfChange(opt as ISelectedValue)}
                  placeholder="Selecionar UF"
                  styles={selectedTheme === 'dark' ? CustomStyleDarkTheme : CustomStyle}
                  classNamePrefix="react-select"
                  className="react-select-container"
                />
              </FormGroup>
              <FormGroup>
                <label htmlFor="contactName">Selecione o VT</label>
                <Select
                  value={selectedItem}
                  options={vtOptionsBySelectedUf}
                  onChange={(opt) => handleSelectedItemChange(opt as ISelectedValue)}
                  placeholder="Selecionar VT"
                  styles={selectedTheme === 'dark' ? CustomStyleDarkTheme : CustomStyle}
                  classNamePrefix="react-select"
                  className="react-select-container"
                />
              </FormGroup>
            </>
          )}

          {manualRoutingType === 'detailed' && (
            <>
              {instruction === 'Transporte Público' && (
                <FormGroup error={getErrorMessageByFieldName('transportLine')}>
                  <label htmlFor="contactName">Linha do Transporte (Letreiro / Código)</label>
                  <Input
                    value={transportLine}
                    onChange={handleTransportLineChange}
                    placeholder="Linha que o passageiro usará para percorrer o trecho desta instrução"
                    error={getErrorMessageByFieldName('transportLine')}
                    maxLength={100}
                  />
                </FormGroup>
              )}
              <AsideContainer>
                <FormGroup error={getErrorMessageByFieldName('instructionOrigin')}>
                  <label htmlFor="contactName">Origem Instrução</label>
                  <Input
                    value={instructionOrigin}
                    onChange={handleInstructionOriginChange}
                    placeholder="Endereço de origem do trecho dessa instrução"
                    error={getErrorMessageByFieldName('instructionOrigin')}
                    maxLength={100}
                  />
                </FormGroup>
                <FormGroup error={getErrorMessageByFieldName('instructionDestiny')}>
                  <label htmlFor="contactName">Destino Instrução</label>
                  <Input
                    value={instructionDestiny}
                    onChange={handleInstructionDestinyChange}
                    placeholder="Endereço de destino do trecho dessa instrução"
                    error={getErrorMessageByFieldName('instructionDestiny')}
                    maxLength={100}
                  />
                </FormGroup>
              </AsideContainer>

              <AsideContainer>
                <FormGroup error={getErrorMessageByFieldName('instructionDistance')}>
                  <label htmlFor="contactName">Distância</label>
                  <Input
                    value={instructionDistance}
                    onChange={handleInstructionDistanceChange}
                    placeholder="Distância do trecho em metros dessa instrução"
                    error={getErrorMessageByFieldName('instructionDistance')}
                    maxLength={100}
                  />
                </FormGroup>
                <FormGroup error={getErrorMessageByFieldName('instructionTime')}>
                  <label htmlFor="contactName">Tempo</label>
                  <Input
                    value={instructionTime}
                    onChange={handleInstructionTimeChange}
                    placeholder="Tempo do trecho em minutos dessa instrução"
                    error={getErrorMessageByFieldName('instructionTime')}
                    maxLength={100}
                  />
                </FormGroup>
              </AsideContainer>
            </>
          )}
        </>
      )}
    />
  );
}
